import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  BoltIcon,
  RocketLaunchIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  ArrowRightIcon,
  SparklesIcon,
  ClipboardDocumentCheckIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import { listPublicBootcamps } from '../../api/bootcampApi';

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

const STATUS_STYLES = {
  active:    'bg-emerald-100 text-emerald-700 ring-emerald-200',
  completed: 'bg-blue-100 text-blue-700 ring-blue-200',
  draft:     'bg-amber-100 text-amber-700 ring-amber-200',
};

// ── Feature card (static) ─────────────────────────────────────────────────────
function FeatureCard({ icon: Icon, title, desc, color }) {
  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-white/60 bg-white/70 backdrop-blur-sm p-6 shadow-sm">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}>
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-neutral-900">{title}</h3>
        <p className="mt-1 text-sm text-neutral-500 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

// ── Bootcamp card ─────────────────────────────────────────────────────────────
function BootcampCard({ bootcamp }) {
  const startDate   = fmtDate(bootcamp.start_date);
  const endDate     = fmtDate(bootcamp.end_date);
  const statusStyle = STATUS_STYLES[bootcamp.status] ?? STATUS_STYLES.draft;
  const count       = parseInt(bootcamp.assessment_count ?? 0, 10);
  const bid         = bootcamp.bootcamp_id ?? bootcamp.id;

  return (
    <Link
      to={`/bootcamp/${bid}`}
      className="group flex flex-col rounded-2xl border border-neutral-200 bg-white shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden"
    >
      {/* Top accent bar */}
      <div className="h-1.5 w-full bg-gradient-to-r from-primary-500 via-secondary-500 to-primary-400" />

      <div className="flex flex-col flex-1 p-6 gap-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-bold text-neutral-900 font-heading leading-snug truncate group-hover:text-primary-700 transition-colors">
              {bootcamp.title}
            </h3>
            {bootcamp.cohort_name && (
              <p className="mt-0.5 text-xs font-medium text-primary-600 truncate">
                {bootcamp.cohort_name}
              </p>
            )}
          </div>
          <span className={`shrink-0 mt-0.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 capitalize ${statusStyle}`}>
            {bootcamp.status}
          </span>
        </div>

        {/* Organization */}
        {bootcamp.organization_name && (
          <p className="text-xs text-neutral-400 -mt-2">{bootcamp.organization_name}</p>
        )}

        {/* Description */}
        {bootcamp.description && (
          <p className="text-sm text-neutral-600 leading-relaxed line-clamp-2">
            {bootcamp.description}
          </p>
        )}

        {/* Meta */}
        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-neutral-500 pt-2 border-t border-neutral-100">
          {(startDate || endDate) && (
            <span className="flex items-center gap-1.5">
              <CalendarDaysIcon className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
              {startDate && endDate
                ? `${startDate} – ${endDate}`
                : startDate
                ? `Starts ${startDate}`
                : `Ends ${endDate}`}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <ClipboardDocumentCheckIcon className="h-3.5 w-3.5 shrink-0 text-neutral-400" />
            {count} assessment{count !== 1 ? 's' : ''}
          </span>
          <span className="ml-auto font-medium text-primary-600 group-hover:underline">
            View details →
          </span>
        </div>
      </div>
    </Link>
  );
}

// ── Stat chip ─────────────────────────────────────────────────────────────────
function StatChip({ value, label }) {
  return (
    <div className="flex flex-col items-center px-6 py-4">
      <span className="text-3xl font-extrabold font-heading text-white">{value}</span>
      <span className="mt-0.5 text-sm text-primary-200">{label}</span>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [bootcamps, setBootcamps] = useState([]);
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    listPublicBootcamps()
      .then((res) => setBootcamps(res.data?.data ?? res.data ?? []))
      .catch(() => setBootcamps([]))
      .finally(() => setLoading(false));
  }, []);

  // Redirect authenticated users directly to the dashboard (after all hooks)
  if (!authLoading && isAuthenticated) return <Navigate to="/dashboard" replace />;

  const activeCount    = bootcamps.filter((b) => b.status === 'active').length;
  const assessmentCount = bootcamps.reduce((s, b) => s + parseInt(b.assessment_count ?? 0, 10), 0);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-primary-50 font-sans">

      {/* ── Navbar ── */}
      <header className="sticky top-0 z-30 border-b border-white/80 bg-white/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          {/* Brand */}
          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary-600">
              <BoltIcon className="h-5 w-5 text-white" />
            </div>
            <span className="text-base font-bold font-heading text-neutral-900 tracking-tight">
              FounderMind <span className="text-primary-600">AI</span>
            </span>
          </div>
          {/* Login */}
          <button
            onClick={() => navigate('/login')}
            className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-700 active:bg-primary-800 transition-colors"
          >
            Login
            <ArrowRightIcon className="h-3.5 w-3.5" />
          </button>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-700 via-primary-600 to-secondary-700 py-20 sm:py-28 lg:py-36">
        {/* Decorative blobs */}
        <div className="pointer-events-none absolute -top-32 -right-32 h-96 w-96 rounded-full bg-white/5 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 -left-20 h-72 w-72 rounded-full bg-secondary-500/20 blur-3xl" />
        <div className="pointer-events-none absolute top-1/2 left-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/5 blur-3xl" />

        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6 lg:px-8">
          {/* Badge */}
          <span className="mb-6 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-1.5 text-xs font-semibold text-white ring-1 ring-white/20">
            <SparklesIcon className="h-3.5 w-3.5" />
            Powered by AI Assessment Technology
          </span>

          <h1 className="text-4xl font-extrabold font-heading tracking-tight text-white sm:text-5xl lg:text-6xl">
            Bootcamp Assessment
            <br />
            <span className="bg-gradient-to-r from-yellow-300 to-orange-300 bg-clip-text text-transparent">
              Portal
            </span>
          </h1>

          <p className="mx-auto mt-6 max-w-2xl text-base text-primary-100 leading-relaxed sm:text-lg">
            Discover active bootcamps and cohort programs. Our AI-powered platform evaluates
            founder potential across mindset, execution, innovation, and leadership.
          </p>

          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-sm font-bold text-primary-700 shadow-lg hover:bg-primary-50 active:bg-primary-100 transition-colors"
            >
              Login to Dashboard
              <ArrowRightIcon className="h-4 w-4" />
            </button>
            <a
              href="#bootcamps"
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white transition-colors"
            >
              <RocketLaunchIcon className="h-4 w-4" />
              View Bootcamps
            </a>
          </div>
        </div>

        {/* Stats bar */}
        {bootcamps.length > 0 && (
          <div className="relative mx-auto mt-16 max-w-2xl px-4 sm:px-6">
            <div className="flex divide-x divide-white/20 rounded-2xl bg-white/10 backdrop-blur-sm ring-1 ring-white/20">
              <StatChip value={bootcamps.length} label="Bootcamps" />
              <StatChip value={activeCount}      label="Active Now" />
              <StatChip value={assessmentCount}  label="Assessments" />
            </div>
          </div>
        )}
      </section>

      {/* ── Features strip ── */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <FeatureCard
            icon={SparklesIcon}
            title="AI-Powered Evaluation"
            desc="Every submission is analysed by GPT-4o, scoring founders across 5 key dimensions with detailed rationale."
            color="bg-primary-600"
          />
          <FeatureCard
            icon={ChartBarIcon}
            title="Deep Insights"
            desc="Dimension breakdowns, per-question feedback, key insights, and ranked participant reports."
            color="bg-secondary-600"
          />
          <FeatureCard
            icon={UserGroupIcon}
            title="Cohort Management"
            desc="Organise participants by bootcamp and cohort. Track progress from submission to analysis."
            color="bg-violet-600"
          />
        </div>
      </section>

      {/* ── Bootcamps grid ── */}
      <section id="bootcamps" className="mx-auto max-w-7xl px-4 pb-24 sm:px-6 lg:px-8">
        <div className="mb-10 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2 className="text-2xl font-bold font-heading text-neutral-900 sm:text-3xl">
              Bootcamp Programs
            </h2>
            <p className="mt-1 text-sm text-neutral-500">
              All programmes and cohorts on this platform.
            </p>
          </div>
          <button
            onClick={() => navigate('/login')}
            className="mt-4 inline-flex shrink-0 items-center gap-2 rounded-full border border-primary-200 bg-primary-50 px-5 py-2 text-sm font-semibold text-primary-700 hover:bg-primary-100 transition-colors sm:mt-0"
          >
            Manage Bootcamps →
          </button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-52 rounded-2xl bg-neutral-100 animate-pulse" />
            ))}
          </div>
        ) : bootcamps.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border-2 border-dashed border-neutral-200 bg-white py-20 text-center">
            <RocketLaunchIcon className="h-12 w-12 text-neutral-300" />
            <div>
              <p className="text-sm font-medium text-neutral-600">No active bootcamps yet</p>
              <p className="mt-1 text-xs text-neutral-400">
                Login to create and manage your first bootcamp.
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="inline-flex items-center gap-2 rounded-full bg-primary-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
            >
              Get Started
              <ArrowRightIcon className="h-3.5 w-3.5" />
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {bootcamps.map((b) => (
              <BootcampCard key={b.bootcamp_id ?? b.id} bootcamp={b} />
            ))}
          </div>
        )}
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-neutral-200 bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-8 text-sm text-neutral-400 sm:flex-row sm:px-6 lg:px-8">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary-600">
              <BoltIcon className="h-3.5 w-3.5 text-white" />
            </div>
            <span className="font-semibold text-neutral-600 font-heading">FounderMind AI</span>
          </div>
          <p>© {new Date().getFullYear()} FounderMind AI. All rights reserved.</p>
          <button
            onClick={() => navigate('/login')}
            className="text-primary-600 hover:text-primary-700 font-medium transition-colors"
          >
            Admin Login →
          </button>
        </div>
      </footer>
    </div>
  );
}
